import React, {useEffect, useState} from 'react';
import {create, InstanceProps} from 'react-modal-promise';

import {ModalReact, ModalType} from 'common/components/modal';
import {withStyles} from 'common/util/react';
import {IconSizeType} from 'common/constants';
import {epochTimestampToProjectTimeZone} from 'common/util/format-property';
import {DEFAULT_USER_NAME} from 'common/report/util/profile/types';
import {SpinnerReact} from 'common/components/spinner';
import {SpinnerSize} from 'common/components/spinner/types';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {reporter} from 'common/report/error-reporting';
import {getProfilePath} from 'common/report/util/profile/utils';
import {UserLocalStorageHelper} from 'common/report/user-local-storage';
import {LocalStorageKey} from 'common/report/constants';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {IronContextRegistry} from 'common/report/context-registry';
import {Visibility} from 'common/components/base/stackable';
import {ButtonTheme} from 'common/components/button/constants';
import {TableReact} from 'common/components/table';
import {InputReact} from 'common/components/input';
import {InputSize} from 'common/components/input/types';
import {SelectReact} from 'common/components/select';
import {ButtonReact} from 'common/components/button';

import {TrackedUserIconReact} from '../tracked-user-icon';
import {CopyToClipboardButtonReact} from '../copy-to-clipboard/copy-to-clipboard-button';
import {CopyType} from '../copy-to-clipboard/copy-to-clipboard-button/types';
import {promptFeedbackModal} from '../feedback-modal';
import {FeedbackForFeature, Sentiment} from '../feedback-modal/types';
import './index.styl';
import styles from './index.module.styl';
import css from './index.module.styl?inline';
import {RecordingMetadata, RecordingPlayerSource, RecordingPlayerTrackingEvent} from './types';
import {RecordingPlayer} from './player';
import {ReplayPlaylistRowEl} from './playlist-row';
import {fetchEventsForReplays} from './query-utils';
import {ReplayPlaylistRowEventMetadataEl} from './playlist-row-events';
import {getRelevantReplayEvents, getReplayEventCount} from './util';

const PERSISTENCE_KEY = `replay-modal-persistence-key`;

type RecordingPlaylistPlayerProps = {
  replays: RecordingMetadata[];
  open: boolean;
  onClose: () => void;
  source: RecordingPlayerSource;
  loadingReplays?: boolean; // Used to indicate that the replays are still being queried outside the component
  enableUpdateURL?: boolean; // Used to enable URL updating feature
  disablePlaylist?: boolean; // Used to disable the playlist feature when we are just displaying a single replay
  extraTrackingProps?: Record<string, any>;
} & Pick<IronContextRegistry, 'localStorageManager'>;

type PersistedState = {
  seenReplayIds: string[];
};

enum SortOption {
  MostRecent = `Most Recent`,
  LeastRecent = `Least Recent`,
  LongestDuration = `Longest Duration`,
  ShortestDuration = `Shortest Duration`,
  MostEvents = `Most Events`,
  LeastEvents = `Least Events`,
}

export function RecordingPlaylistPlayer(props: RecordingPlaylistPlayerProps) {
  const [currentRecordingIdx, setCurrentRecordingIdx] = useState<number>(0);
  const [viewedRecordings, setViewedRecordings] = useState<Set<string>>(new Set());
  const [searchFilter, setSearchFilter] = useState<string>(``);
  const [selectedSortOption, setSelectedSortOption] = useState<SortOption>(SortOption.MostRecent);
  const [visibleRecordingsCount, setVisibleRecordingsCount] = useState<number>(props.replays.length);
  const [playlistCollapsed, setPlaylistCollapsed] = useState<boolean>(false);
  const [sortedReplays, setSortedReplays] = useState<RecordingMetadata[]>([]);
  const [recordingsEventMetadataLoading, setRecordingsEventMetadataLoading] = useState<boolean>(false);
  const [currentRecordingProgressTime, setCurrentRecordingProgressTime] = useState<number>(0);
  const [currentRecordingEventSelectedTime, setCurrentRecordingEventSelectedTime] = useState<number>(null);
  const [expandedRowsReplayIds, setExpandedRowsReplayIds] = useState<Set<string>>(new Set());

  const extraTrackingProps = props.extraTrackingProps || {};

  const currentProject = useCurrentProject();
  const userLocalStorageHelper: UserLocalStorageHelper<PersistedState> = new UserLocalStorageHelper({
    context: props.localStorageManager,
    namespace: LocalStorageKey.SeenReplayIds,
  });

  useEffect(() => {
    setViewedRecordings(getPlayedReplays());
  }, []);

  useEffect(() => {
    const newSortedReplays = sortReplaysBySortOption(props.replays);
    setSortedReplays(newSortedReplays);
    if (newSortedReplays.length > 0) {
      setRecordingsEventMetadataLoading(true);
      fetchEventMetadataForReplays(newSortedReplays);
    }
  }, [props.replays]);

  async function fetchEventMetadataForReplays(replays: RecordingMetadata[]) {
    const populatedReplays = await fetchEventsForReplays(replays);

    setSortedReplays(sortReplaysBySortOption(populatedReplays));
    setRecordingsEventMetadataLoading(false);
  }

  function hasBeenPlayed(replayId: string): boolean {
    return viewedRecordings.has(replayId);
  }

  function updateSearchFilter(newValue: string) {
    setSearchFilter(newValue);
    if (!newValue) {
      setExpandedRowsReplayIds(new Set());
    } else {
      const allReplayIds = sortedReplays.map((replay) => replay.replayId);
      setExpandedRowsReplayIds(new Set(allReplayIds));
    }
  }

  function updateSortOption(newValue: SortOption) {
    setSelectedSortOption(newValue);
    if (sortedReplays.length > 0) {
      const newSortedReplays = sortReplaysBySortOption(sortedReplays, newValue);
      const newIndexOfCurrentReplay = newSortedReplays.findIndex(
        (replay) => replay.replayId === sortedReplays[currentRecordingIdx]?.replayId,
      );
      setSortedReplays(newSortedReplays);
      setCurrentRecordingIdx(newIndexOfCurrentReplay);
    }
  }

  function isSortOptionAscending(option: SortOption) {
    return (
      option === SortOption.LeastRecent || option === SortOption.ShortestDuration || option === SortOption.LeastEvents
    );
  }

  function sortReplaysBySortOption(currentReplays: RecordingMetadata[], currentSortOption = selectedSortOption) {
    const newSortedReplays = [...currentReplays];
    switch (currentSortOption) {
      case SortOption.MostRecent:
        return newSortedReplays.sort((a, b) => b.time - a.time);
      case SortOption.LeastRecent:
        return newSortedReplays.sort((a, b) => a.time - b.time);
      case SortOption.LongestDuration:
        return newSortedReplays.sort((a, b) => b.duration - a.duration);
      case SortOption.ShortestDuration:
        return newSortedReplays.sort((a, b) => a.duration - b.duration);
      case SortOption.MostEvents:
        return newSortedReplays.sort((a, b) => getReplayEventCount(b) - getReplayEventCount(a));
      case SortOption.LeastEvents:
        return newSortedReplays.sort((a, b) => getReplayEventCount(a) - getReplayEventCount(b));
    }
  }

  function persistPlayedReplay(replayId: string): void {
    const parsedValue = getPlayedReplays();
    try {
      const updatedValue = [...parsedValue, replayId];
      userLocalStorageHelper.setItem({
        key: PERSISTENCE_KEY,
        userId: userCookieUtils.getUserIdentityFromCookie()?.id,
        value: {seenReplayIds: updatedValue},
      });
      setViewedRecordings(new Set(updatedValue));
    } catch (err) {
      reporter.warn(err, `Unable to persist ${PERSISTENCE_KEY} to localStorage.`);
    }
  }

  function getPlayedReplays(): Set<string> {
    try {
      const persistedState = userLocalStorageHelper.getItem({
        key: PERSISTENCE_KEY,
        userId: userCookieUtils.getUserIdentityFromCookie()?.id,
      });
      return new Set(persistedState?.seenReplayIds) || new Set();
    } catch (err) {
      reporter.warn(err, `Unable to retrieve ${PERSISTENCE_KEY} value from localStorage.`);
      return new Set();
    }
  }

  function updateURL(distinctId: string, replayId: string | null) {
    if (props.enableUpdateURL) {
      window.history.replaceState(null, null, getProfilePath(distinctId, null, replayId));
    }
  }

  function getDisplayTime(secondsFromEpoch: number, timezone: string, timeOnly = false) {
    if (!secondsFromEpoch) {
      return `Unknown time`;
    }
    return epochTimestampToProjectTimeZone(secondsFromEpoch * 1000, timezone).format(
      timeOnly ? `h:mmA` : `MMM D, h:mmA`,
    );
  }

  function getDisplayName(email: string, name: string) {
    if (email && name === DEFAULT_USER_NAME) {
      return email;
    }

    return name;
  }

  function findReplayIdx(replayId: string) {
    return sortedReplays.findIndex((replay) => replay.replayId === replayId);
  }

  function playNextRecording(replayId: string) {
    const replayIdx = findReplayIdx(replayId);
    const nextRecordingIdx = replayIdx + 1;

    if (!searchFilter && nextRecordingIdx < sortedReplays.length && nextRecordingIdx >= 0) {
      setCurrentRecordingIdx(nextRecordingIdx);
      setCurrentRecordingEventSelectedTime(null);
    }
  }

  function copyReplayLink(distinctId: string, replayId: string) {
    return window.location.origin + getProfilePath(distinctId, null, replayId);
  }

  function jumpToEventTime(time: number, replayId: string) {
    if (replayId === sortedReplays[currentRecordingIdx]?.replayId) {
      setCurrentRecordingEventSelectedTime(time);
    } else {
      const replayIdx = findReplayIdx(replayId);
      setCurrentRecordingIdx(replayIdx);
      setCurrentRecordingEventSelectedTime(time);
    }
  }

  function onToggleExpand(replayId: string) {
    const newExpandedRowsReplayIds = new Set(expandedRowsReplayIds);
    if (newExpandedRowsReplayIds.has(replayId)) {
      newExpandedRowsReplayIds.delete(replayId);
    } else {
      newExpandedRowsReplayIds.add(replayId);
    }
    setExpandedRowsReplayIds(newExpandedRowsReplayIds);
  }

  function showFeedbackModal(sentiment: Sentiment) {
    promptFeedbackModal(
      {
        feature: FeedbackForFeature.SessionReplay,
        'negative-label': ``,
        placeholder: `Additional feedback (Optional)`,
        'sentiment-heading': `Rate your satisfaction with this replay`,
        'positive-label': ``,
        title: `Replay Feedback`,
      },
      {
        extraFeedbackEventName: RecordingPlayerTrackingEvent.Feedback,
        extraFeedbackEventProperties: {
          ...extraTrackingProps,
          replay_id: sortedReplays[currentRecordingIdx]?.replayId,
          replay_distinct_id: sortedReplays[currentRecordingIdx]?.distinctId,
          source: props.source,
        },
        initialSentiment: sentiment,
      },
    );
  }

  function getSortFromOption(option: SortOption) {
    switch (option) {
      case SortOption.MostRecent:
        return {field: `time`, order: -1};
      case SortOption.LeastRecent:
        return {field: `time`, order: 1};
      case SortOption.LongestDuration:
        return {field: `duration`, order: -1};
      case SortOption.ShortestDuration:
        return {field: `duration`, order: 1};
      case SortOption.MostEvents:
        return {field: `eventCount`, order: -1};
      case SortOption.LeastEvents:
        return {field: `eventCount`, order: 1};
      default:
        return {field: `time`, order: -1};
    }
  }

  function getClosedReplayAreaState(currentRecording: RecordingMetadata, displayName: string) {
    return (
      (playlistCollapsed || props.disablePlaylist) && (
        <div className={styles.closedPlaylistInfo}>
          {!props.disablePlaylist && <ButtonReact icon='push-right' onClick={() => setPlaylistCollapsed(false)} />}
          {currentRecording && (
            <>
              <a href={getProfilePath(currentRecording.distinctId, null)} target='_blank'>
                <TrackedUserIconReact
                  distinctId={currentRecording.distinctId}
                  imgSrc={currentRecording.avatarUrl}
                  email={currentRecording.email}
                  size={IconSizeType.Small}
                />
              </a>
              <div className={styles.closedPlaylistInfoText}>
                <div className={styles.closedPlaylistInfoTextTitle}>
                  {displayName !== DEFAULT_USER_NAME ? displayName : `User Distinct ID: ${currentRecording.distinctId}`}
                </div>
                {currentRecording.time && (
                  <div className={styles.closedPlaylistInfoTextSubtitle}>
                    {getDisplayTime(currentRecording.time, currentProject.get().value?.timezone)}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )
    );
  }

  function getReplayAreaState() {
    if (sortedReplays.length === 0) {
      if (props.loadingReplays) {
        return <SpinnerReact className={styles.loadingSpinner} size={SpinnerSize.Large} />;
      } else {
        return (
          <>
            {getClosedReplayAreaState(null, ``)}
            <div className={styles.emptyState}>
              <div className={styles.emptyStateTitle}>No replays available</div>
              <div className={styles.emptyStateSubtitle}>
                We couldn't find any replays for the specified conditions. See{` `}
                <a href={`https://docs.mixpanel.com/docs/features/session-replay#faq`} target={`_blank`}>
                  our docs
                </a>
                {` `}
                for further details.
              </div>
            </div>
          </>
        );
      }
    } else {
      const currentRecording = sortedReplays[currentRecordingIdx];
      const displayName = getDisplayName(currentRecording.email, currentRecording.name);

      return (
        <>
          {getClosedReplayAreaState(currentRecording, displayName)}
          <div className={styles.feedbackContainer}>
            <ButtonReact icon='thumbs-up' onClick={() => showFeedbackModal(Sentiment.Positive)} />
            <ButtonReact icon='thumbs-down' onClick={() => showFeedbackModal(Sentiment.Negative)} />
          </div>
        </>
      );
    }
  }

  function getPlaylist() {
    const sortInfo = getSortFromOption(selectedSortOption);

    return (
      <div className={`${styles.replaysListItemContainer}`} role={`rowgroup`}>
        <TableReact
          emptyMessage='No replays found.'
          loading={props.loadingReplays}
          defaultSortField={sortInfo.field}
          defaultSortOrder={sortInfo.order}
          searchFilter={searchFilter}
          rows={sortedReplays.map((replay) => ({
            ...replay,
            isExpanded: expandedRowsReplayIds.has(replay.replayId),
            eventCount: getReplayEventCount(replay),
          }))}
          columns={[
            {
              field: `name`,
              cellRenderer: (replay: RecordingMetadata) =>
                ReplayPlaylistRowEl({
                  sortedReplays,
                  currentRecordingIdx,
                  hasBeenPlayed: hasBeenPlayed(replay.replayId),
                  replay,
                  displayName: getDisplayName(replay.email, replay.name),
                  displayTime: getDisplayTime(replay.time, currentProject.get().value?.timezone),
                  searchFilter,
                  isExpanded: expandedRowsReplayIds.has(replay.replayId),
                  onToggleExpand,
                }),
              labelTransform: (row: RecordingMetadata, _field) => {
                const relevantEvents = getRelevantReplayEvents(row);
                const eventNames = new Set(relevantEvents.map((event) => event.eventName));
                const eventNamesString = Array.from(eventNames).join(` `);
                return `${getDisplayName(row.email, row.name)} ${getDisplayTime(
                  row.time,
                  currentProject.get().value?.timezone,
                )} ${row.distinctId} ${eventNamesString}`;
              },
              isSearchable: true,
            },
          ]}
          rowDetailsRenderer={(replay: RecordingMetadata) =>
            ReplayPlaylistRowEventMetadataEl({
              loadingEventMetadata: recordingsEventMetadataLoading,
              replay,
              getDisplayTime: (time: number) => getDisplayTime(time, currentProject.get().value?.timezone, true),
              jumpToEvent: jumpToEventTime,
              currentRecordingProgressTime:
                replay.replayId === sortedReplays[currentRecordingIdx]?.replayId ? currentRecordingProgressTime : null,
              searchFilter,
            })
          }
          windowScroll={false}
          hideTitles
          onRowClick={({detail}) => {
            const replayIdx = findReplayIdx(detail.row.replayId as string);
            setCurrentRecordingIdx(replayIdx);
            setCurrentRecordingEventSelectedTime(null);
          }}
          onNumResultsChange={({detail}) => {
            setVisibleRecordingsCount(detail.numResults);
          }}
          disableExpandableRows
          disableExpandableRowsIndent
        />
      </div>
    );
  }

  const currentRecording = sortedReplays[currentRecordingIdx];

  return (
    <div>
      <ModalReact
        modalType={ModalType.Modal}
        open={props.open}
        growToContent
        closeable
        onChange={({detail}) => {
          if (detail.state === Visibility.Closed) {
            if (sortedReplays.length > 0) {
              updateURL(currentRecording.distinctId, null);
            }
            mixpanel.track(RecordingPlayerTrackingEvent.Close, {
              ...extraTrackingProps,
              replay_id: currentRecording?.replayId,
              replay_distinct_id: currentRecording?.distinctId,
              source: props.source,
            });
            props.onClose();
          } else if (detail.state === Visibility.Open) {
            if (sortedReplays.length > 0) {
              updateURL(currentRecording.distinctId, currentRecording.replayId);
            }
            mixpanel.track(RecordingPlayerTrackingEvent.Open, {
              ...extraTrackingProps,
              replay_id: currentRecording?.replayId,
              replay_distinct_id: currentRecording?.distinctId,
              source: props.source,
            });
          }
        }}
      >
        <div className={styles.recordingModal}>
          {!props.disablePlaylist && !playlistCollapsed && (
            <div className={`${styles.replaysList}`}>
              <div className={`${styles.replaysListHeader}`}>
                <div className={styles.replaysListHeaderText}>
                  <div className={styles.replaysListHeaderTitle}>Replays</div>
                  {!props.loadingReplays && (
                    <div className={styles.replaysListHeaderSubtitle}>{visibleRecordingsCount} replays</div>
                  )}
                </div>
                <ButtonReact icon='push-left' onClick={() => setPlaylistCollapsed(true)} />
              </div>
              <div className={`${styles.replaysListControls}`}>
                <InputReact
                  icon={`search`}
                  placeholder={`Search for replays`}
                  size={InputSize.Medium}
                  value={searchFilter}
                  onInput={(ev) => updateSearchFilter(ev.target.value)}
                />
                <SelectReact
                  iconOnly
                  icon={{
                    leftIcon: isSortOptionAscending(selectedSortOption) ? `sort-value-asc` : `sort-value-desc`,
                  }}
                  menuWidth={150}
                  options={Object.values(SortOption)}
                  onChange={(ev) => {
                    updateSortOption(ev.detail.value as SortOption);
                  }}
                  selectedOption={selectedSortOption}
                />
              </div>
              {getPlaylist()}
            </div>
          )}
          <RecordingPlayer
            replay={currentRecording}
            onStartReplay={(replayId) => {
              const replayIdx = findReplayIdx(replayId);
              updateURL(sortedReplays[replayIdx].distinctId, replayId);
              mixpanel.track(RecordingPlayerTrackingEvent.Watch, {
                ...extraTrackingProps,
                replay_id: replayId,
                replay_distinct_id: sortedReplays[replayIdx].distinctId,
                source: props.source,
              });
            }}
            onFinishReplay={(replayId) => {
              persistPlayedReplay(replayId);
              playNextRecording(replayId);
              mixpanel.track(RecordingPlayerTrackingEvent.Finish, {
                ...extraTrackingProps,
                replay_id: replayId,
                replay_distinct_id: sortedReplays[findReplayIdx(replayId)].distinctId,
                source: props.source,
              });
            }}
            updateCurrentReplayEvent={(time) => {
              setCurrentRecordingProgressTime(time);
              setCurrentRecordingEventSelectedTime(null);
            }}
            currentRecordingEventSelectedTime={currentRecordingEventSelectedTime}
            copyButton={
              sortedReplays.length !== 0 && (
                <CopyToClipboardButtonReact
                  className={styles.copyButton}
                  copyType={CopyType.Link}
                  value={copyReplayLink(currentRecording.distinctId, currentRecording.replayId)}
                  theme={ButtonTheme.Tertiary}
                />
              )
            }
          >
            {getReplayAreaState()}
          </RecordingPlayer>
        </div>
      </ModalReact>
    </div>
  );
}

export const RecordingPlaylistPlayerStyled = withStyles(RecordingPlaylistPlayer, css);

export type RecordingPlayerModalTypes = {
  replays: RecordingMetadata[];
  source: RecordingPlayerSource;
  loadingReplays?: boolean;
  emptyReplays?: boolean;
  enableUpdateURL?: boolean;
  disablePlaylist?: boolean;
  extraTrackingProps?: Record<string, any>;
};
type RecordingPlayerModalProps = RecordingPlayerModalTypes &
  InstanceProps<unknown> &
  Pick<IronContextRegistry, 'localStorageManager'>;

const RecordingPlayerModalWrapper: React.FC<RecordingPlayerModalProps> = (props: RecordingPlayerModalProps) => {
  return (
    <RecordingPlaylistPlayerStyled
      replays={props.replays}
      open={props.isOpen}
      onClose={props.onReject}
      source={props.source}
      loadingReplays={props.loadingReplays || false}
      enableUpdateURL={props.enableUpdateURL || false}
      disablePlaylist={props.disablePlaylist || false}
      localStorageManager={props.localStorageManager}
    />
  );
};

// Wrapper where you can pass in a promise to resolve the replays
type RecordingPlayerModalAsyncProps = Omit<RecordingPlayerModalProps, 'replays' | 'loadingReplays'> & {
  replaysPromise: Promise<RecordingMetadata[]>;
};
const RecordingPlayerModalAsyncWrapper: React.FC<RecordingPlayerModalAsyncProps> = (
  props: RecordingPlayerModalAsyncProps,
) => {
  const [replays, setReplays] = useState<RecordingMetadata[]>([]);
  const [loadingReplays, setLoadingReplays] = useState<boolean>(true);

  useEffect(() => {
    const startedAt = new Date().getTime();
    props.replaysPromise
      .then((replays) => {
        setReplays(replays);
        setLoadingReplays(false);
        mixpanel.track(RecordingPlayerTrackingEvent.ListLoaded, {
          ...props.extraTrackingProps,
          source: props.source,
          'Load time (ms)': new Date().getTime() - startedAt,
        });
      })
      .catch((err) => {
        reporter.error(err);
        setLoadingReplays(false);
        mixpanel.track(RecordingPlayerTrackingEvent.ListLoadFailed, {
          ...props.extraTrackingProps,
          source: props.source,
          error: err,
          'Load time (ms)': new Date().getTime() - startedAt,
        });
      });
  }, []);

  return (
    <RecordingPlaylistPlayerStyled
      replays={replays}
      open={props.isOpen}
      onClose={props.onReject}
      source={props.source}
      loadingReplays={loadingReplays}
      enableUpdateURL={props.enableUpdateURL || false}
      disablePlaylist={props.disablePlaylist || false}
      localStorageManager={props.localStorageManager}
      extraTrackingProps={props.extraTrackingProps}
    />
  );
};

export const RecordingPlayerModal = create(RecordingPlayerModalWrapper);
export const RecordingPlayerModalAsync = create(RecordingPlayerModalAsyncWrapper);
